import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet as RouterOutlet, matchPath, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { WorkspaceTabsProvider, useWorkspaceTabs } from './context/WorkspaceTabsContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import WorkspaceTabBar from './components/WorkspaceTabBar';
import { AppDialogProvider } from './components/AppDialog';
import Dashboard from './pages/Dashboard';
import GameCurrencies from './pages/games/GameCurrencies';
import GameCategories from './pages/games/GameCategories';
import GameSubCategories from './pages/games/GameSubCategories';
import GameCountries from './pages/games/GameCountries';
import GameSources from './pages/games/GameSources';
import PaymentGatewayRecords from './pages/paymentGateways/PaymentGatewayRecords';
import Merchants from './pages/Merchants';
import MerchantSearch from './pages/MerchantSearch';
import MerchantDetail from './pages/MerchantDetail';
import MerchantView from './pages/MerchantView';
import Providers from './pages/games/Providers';
import MasterProviders from './pages/games/GameProviders';
import MasterProviderView from './pages/games/MasterProviderView';
import Users from './pages/Users';
import UserDetail from './pages/UserDetail';
import Profile from './pages/Profile';
import Logs from './pages/Logs';
import ChangeHistory from './pages/ChangeHistory';
import Settings from './pages/Settings';
import Tickets from './pages/Tickets';
import TicketDetail from './pages/TicketDetail';
import TicketWorkflowSettings from './pages/TicketWorkflowSettings';
import FinanceReconciliation from './pages/FinanceReconciliation';
import FinanceReconciliationDetail from './pages/FinanceReconciliationDetail';
import './components/Layout.css';

const MOBILE_NAV_QUERY = '(max-width: 768px)';

const isMobileNav = () => (
  typeof window !== 'undefined' && window.matchMedia(MOBILE_NAV_QUERY).matches
);

const isDashboardRoute = (path = '') => path.split(/[?#]/)[0] === '/dashboard';

// Professional Layout component
const Layout = () => {
  const location = useLocation();
  const [isMobileLayout, setIsMobileLayout] = useState(isMobileNav);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isMobileNav());
  const { tabs } = useWorkspaceTabs();
  const hasWorkspaceTabs = tabs.length > 0;

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_NAV_QUERY);
    const syncMobileState = () => {
      setIsMobileLayout(mediaQuery.matches);
      setIsSidebarOpen(!mediaQuery.matches);
    };

    syncMobileState();

    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', syncMobileState);
      return () => mediaQuery.removeEventListener('change', syncMobileState);
    }

    mediaQuery.addListener(syncMobileState);
    return () => mediaQuery.removeListener(syncMobileState);
  }, []);

  useEffect(() => {
    if (isMobileLayout) {
      setIsSidebarOpen(false);
    }
  }, [isMobileLayout, location.pathname]);

  return (
    <div className="layout-container">
      {isMobileLayout && isSidebarOpen && (
        <button
          type="button"
          className="sidebar-backdrop"
          aria-label="Close sidebar"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <Sidebar isOpen={isSidebarOpen} />
      
      <div className={`main-wrapper ${isSidebarOpen ? '' : 'full-width'}`} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <WorkspaceTabBar isSidebarOpen={isSidebarOpen} />
        <Header
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={toggleSidebar}
          hasWorkspaceTabs={hasWorkspaceTabs}
        />
        
        <main className={`main-content ${!isSidebarOpen ? 'expanded' : ''} ${hasWorkspaceTabs ? 'with-workspace-tabs' : ''}`}>
          <RouterOutlet />
        </main>
      </div>
    </div>
  );
};

const RootRedirect = () => {
  const { getDefaultRoute } = useAuth();
  return <Navigate to={getDefaultRoute()} replace />;
};

const NotFoundPage = () => (
  <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
    Page not found.
  </div>
);

const tabRoutes = [
  { pattern: '/dashboard', title: 'Dashboard', subtitle: 'Overview', kind: 'dashboard' },
  { pattern: '/merchants', title: 'Merchants', subtitle: 'Management', kind: 'list' },
  { pattern: '/merchant-search', title: 'Merchant Search', subtitle: 'Search conversation', kind: 'search' },
  { pattern: '/merchants/new', title: 'Add Merchant', kind: 'create' },
  { pattern: '/merchants/:id/view', title: 'Merchant View', kind: 'view', subtitleParam: 'id' },
  { pattern: '/merchants/:id', title: 'Edit Merchant', kind: 'edit', subtitleParam: 'id' },
  { pattern: '/users', title: 'Users', subtitle: 'Management', kind: 'list' },
  { pattern: '/users/new', title: 'Add User', kind: 'create' },
  { pattern: '/users/:id', title: 'Edit User', kind: 'edit', subtitleParam: 'id' },
  { pattern: '/logs', title: 'Audit Logs', kind: 'list' },
  { pattern: '/change-history', title: 'Change History', kind: 'list' },
  { pattern: '/finance-reconciliation', title: 'Finance Reconciliation', kind: 'list' },
  { pattern: '/finance-reconciliation/:id', title: 'Finance Report Detail', kind: 'detail', subtitleParam: 'id' },
  { pattern: '/tickets', title: 'Tickets', kind: 'list' },
  { pattern: '/tickets/workflow', title: 'Ticket Workflow', kind: 'settings' },
  { pattern: '/tickets/:id', title: 'Ticket Detail', kind: 'detail', subtitleParam: 'id' },
  { pattern: '/profile', title: 'My Profile', kind: 'detail' },
  { pattern: '/games/currencies', title: 'Game Currencies', kind: 'list' },
  { pattern: '/games/categories', title: 'Game Categories', kind: 'list' },
  { pattern: '/games/sub-categories', title: 'Game Sub Categories', kind: 'list' },
  { pattern: '/games/countries', title: 'Game Countries', kind: 'list' },
  { pattern: '/games/sources', title: 'Provider Sources', kind: 'list' },
  { pattern: '/games/providers', title: 'Game Providers', kind: 'list' },
  { pattern: '/payment-gateways/categories', title: 'Payment Gateway Categories', kind: 'list' },
  { pattern: '/payment-gateways/sources', title: 'Payment Gateway Provider Sources', kind: 'list' },
  { pattern: '/payment-gateways/providers', title: 'Payment Gateway Providers', kind: 'list' },
  { pattern: '/payment-gateways/master-providers', title: 'L1 Payment Gateway Providers', kind: 'list' },
  { pattern: '/games/master-providers', title: 'L1 Providers', kind: 'list' },
  { pattern: '/games/master-providers/:id/view', title: 'L1 Provider View', kind: 'view', subtitleParam: 'id' },
  { pattern: '/transactions', title: 'Transactions', kind: 'list' },
  { pattern: '/settings', title: 'Settings', kind: 'settings' }
];

const getTabMeta = (pathname) => {
  for (const route of tabRoutes) {
    const match = matchPath({ path: route.pattern, end: true }, pathname);
    if (match) {
      return {
        title: route.title,
        subtitle: route.subtitleParam ? match.params[route.subtitleParam] : (route.subtitle || ''),
        kind: route.kind
      };
    }
  }

  return {
    title: 'Backoffice',
    subtitle: pathname,
    kind: 'page'
  };
};

const AppPageRoutes = ({ location }) => (
  <Routes location={location}>
    <Route path="/" element={<RootRedirect />} />
    <Route path="/dashboard" element={<Dashboard />} />
    <Route path="/merchants" element={<Merchants />} />
    <Route path="/merchant-search" element={<MerchantSearch />} />
    <Route path="/merchants/new" element={<MerchantDetail />} />
    <Route path="/merchants/:id" element={<MerchantDetail />} />
    <Route path="/merchants/:id/view" element={<MerchantView />} />
    <Route path="/transactions" element={<div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>Coming Soon</div>} />
    
    <Route path="/users" element={<Users />} />
    <Route path="/users/new" element={<UserDetail />} />
    <Route path="/users/:id" element={<UserDetail />} />
    
    <Route path="/logs" element={<Logs />} />
    <Route path="/change-history" element={<ChangeHistory />} />
    <Route path="/finance-reconciliation" element={<FinanceReconciliation />} />
    <Route path="/finance-reconciliation/:id" element={<FinanceReconciliationDetail />} />
    <Route path="/tickets" element={<Tickets />} />
    <Route path="/tickets/workflow" element={<TicketWorkflowSettings />} />
    <Route path="/tickets/:id" element={<TicketDetail />} />

    <Route path="/profile" element={<Profile />} />

    <Route path="/games/currencies" element={<GameCurrencies />} />
    <Route path="/games/categories" element={<GameCategories />} />
    <Route path="/games/sub-categories" element={<GameSubCategories />} />
    <Route path="/games/countries" element={<GameCountries />} />
    <Route path="/games/sources" element={<GameSources />} />
    <Route path="/games/providers" element={<Providers />} />
    <Route path="/payment-gateways/categories" element={<PaymentGatewayRecords resource="categories" />} />
    <Route path="/payment-gateways/sources" element={<PaymentGatewayRecords resource="sources" />} />
    <Route path="/payment-gateways/providers" element={<PaymentGatewayRecords resource="providers" />} />
    <Route path="/payment-gateways/master-providers" element={<PaymentGatewayRecords resource="master-providers" />} />
    <Route path="/games/master-providers" element={<MasterProviders />} />
    <Route path="/games/master-providers/:id/view" element={<MasterProviderView />} />

    <Route path="/settings" element={<Settings />} />
    <Route path="*" element={<NotFoundPage />} />
  </Routes>
);

const WorkspaceRoutes = () => {
  const location = useLocation();
  const { hasPermission } = useAuth();
  const { tabs, activeTabId, openWorkspaceTab, closeWorkspaceTab } = useWorkspaceTabs();
  const fullPath = `${location.pathname}${location.search}${location.hash}`;
  const canViewDashboard = hasPermission('dashboard', 'view');
  const visibleTabs = canViewDashboard
    ? tabs
    : tabs.filter(tab => !isDashboardRoute(tab.path));

  useEffect(() => {
    if (canViewDashboard) return;

    tabs
      .filter(tab => isDashboardRoute(tab.path))
      .forEach(tab => closeWorkspaceTab(tab.id));
  }, [canViewDashboard, closeWorkspaceTab, tabs]);

  useEffect(() => {
    if (location.pathname === '/') return;
    if (isDashboardRoute(location.pathname) && !canViewDashboard) return;
    const meta = getTabMeta(location.pathname);
    openWorkspaceTab({ path: fullPath, ...meta });
  }, [canViewDashboard, fullPath, location.pathname, openWorkspaceTab]);

  if (location.pathname === '/' || (isDashboardRoute(location.pathname) && !canViewDashboard)) {
    return <RootRedirect />;
  }

  const currentTab = { id: fullPath, path: fullPath, ...getTabMeta(location.pathname) };
  const hasCurrentTab = visibleTabs.some(tab => tab.path === fullPath);
  const hasActiveTab = visibleTabs.some(tab => tab.id === activeTabId);
  const activeId = hasCurrentTab && hasActiveTab ? activeTabId : currentTab.id;
  const renderedTabs = visibleTabs.length > 0
    ? (hasCurrentTab ? visibleTabs : [...visibleTabs, currentTab])
    : [currentTab];

  return (
    <div className="workspace-routes">
      {renderedTabs.map(tab => {
        const isActive = tab.id === activeId || tab.path === fullPath;
        return (
          <section
            key={tab.id}
            style={{ display: isActive ? 'block' : 'none' }}
            aria-hidden={!isActive}
          >
            <AppPageRoutes location={tab.path} />
          </section>
        );
      })}
    </div>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppDialogProvider>
        <BrowserRouter>
          <WorkspaceTabsProvider>
            <Routes>
              <Route path="/login" element={<Login />} />

              <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route path="*" element={<WorkspaceRoutes />} />
              </Route>

              <Route path="*" element={<RootRedirect />} />
            </Routes>
          </WorkspaceTabsProvider>
        </BrowserRouter>
      </AppDialogProvider>
    </AuthProvider>
  );
}

export default App;
